/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { TradeRuleActive } from 'src/components/TradeRule';
import TradeRuleActivePlaceholder from 'src/components/TradeRule/TradeRuleActive/Placeholder';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'trade_rule.trade_rule_active.initial_load_error.message',
    defaultMessage: 'An error occurred while loading the active trade rules. Please try again.',
  },
});

export const tradeRuleActiveRouteQuery = graphql`
  query TradeRuleActiveRoute_Query($uuid: ID!) {
    property(uuid: $uuid) {
      ...TradeRuleActive_property
    }
  }
`;

const tradeRuleActiveRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    params: PropTypes.shape({
      uuid: PropTypes.string,
    }),
  }),
});

/**
 * Renders `TradeRuleActive` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Found-relay route props (match/router/location).
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} trade rule active route content.
 */
function TradeRuleActivePreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(tradeRuleActiveRouteQuery, queryRef);
  return <TradeRuleActive {...routeProps} {...queryData} />;
}

TradeRuleActivePreloadedQueryRenderer.propTypes = {
  routeProps: tradeRuleActiveRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `TradeRuleActive` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} trade rule active route content.
 */
function TradeRuleActiveRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    tradeRuleActiveRouteQuery,
    routeProps,
    { logLabel: 'TradeRuleActiveRoute' },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={TradeRuleActivePlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <TradeRuleActivePreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <TradeRuleActive {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

TradeRuleActiveRouteRenderer.propTypes = {
  routeProps: tradeRuleActiveRoutePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the TradeRuleActive route state supplied by found-relay.
 * Network errors are handled before pending props so the local error boundary mounts.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} TradeRuleActive route content or loading state.
 */
export function tradeRuleActiveRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <TradeRuleActiveRouteRenderer
        key={routeProps.match.params.uuid}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <TradeRuleActiveRouteRenderer
      key={props.match.params.uuid}
      routeProps={props}
    />
  );
}

export default TradeRuleActiveRouteRenderer;
